import Chip from '@mui/material/Chip';
import CircleIcon from '@mui/icons-material/Circle';
import { Link } from 'react-router-dom';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { TONE_DOT_CLASS, TRAFFIC_LIGHT_DOT_CLASSES } from '@/lib/tone';

const previewRows = [
  { label: 'AI resolved — printer offline (site B)', status: 'Auto-drafted', tone: 'ok' as const },
  { label: 'Patch ring 2 — 48 endpoints', status: 'Deployed', tone: 'ok' as const },
  { label: 'M365 backup — Exchange + OneDrive', status: 'Verified', tone: 'ok' as const },
  { label: 'AI call — approval requested', status: 'Escalated', tone: 'watch' as const },
  { label: 'Vuln scan — 2 critical', status: 'Needs approval', tone: 'crit' as const },
];

export function Hero(): JSX.Element {
  return (
    <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-24">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_20%,#000,transparent_75%)]"
      />
      <div
        aria-hidden
        className="animate-drift pointer-events-none absolute -right-32 -top-24 h-[460px] w-[460px] rounded-full bg-nexus-purple/[0.16] blur-3xl"
      />
      <div
        aria-hidden
        className="animate-drift-slow pointer-events-none absolute -left-24 top-40 h-[320px] w-[320px] rounded-full bg-[#b06bff]/[0.12] blur-3xl"
      />
      <div className="container-content relative">
        <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-[1.05fr_0.95fr] md:gap-14">
          <Reveal>
            <Chip
              icon={<CircleIcon sx={{ fontSize: '8px !important', color: '#b06bff' }} />}
              label="MSP-native · Agentic by design"
              size="small"
              className="mb-6 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h1 className="font-display text-4xl font-bold leading-[1.08] tracking-tight text-nexus-ink sm:text-5xl md:text-[54px]">
              The{' '}
              <span className="animate-shimmer bg-gradient-to-r from-[#d3b6ff] via-nexus-purple-light to-[#d3b6ff] bg-clip-text text-transparent">
                agentic
              </span>{' '}
              PSA + RMM platform built to replace your stitched-together stack.
            </h1>
            <p className="mt-6 max-w-[540px] text-lg text-white/70">
              One console for ticketing, monitoring, security, and compliance — with an AI layer
              that drafts, resolves, and prioritizes across every module, always supervised by a
              human before anything ships.
            </p>
            <div id="notify" className="mt-8">
              <NotifyForm />
            </div>
            <p className="mt-3 text-xs text-white/40">
              Live in our own MSP practice today — opening to a limited design-partner
              cohort. No spam, no lists sold. ·{' '}
              <Link to="/roadmap" className="text-white/50 underline hover:text-white/70">
                See what&apos;s building
              </Link>
            </p>
          </Reveal>

          <Reveal delay={150}>
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] shadow-[0_40px_90px_-35px_rgba(139,61,240,0.35)]">
              <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-3">
                {TRAFFIC_LIGHT_DOT_CLASSES.map((cls) => (
                  <span key={cls} className={`h-2.5 w-2.5 rounded-full ${cls}`} />
                ))}
                <span className="ml-2 text-xs text-white/40">app.nexusrmm.ai</span>
                <span className="ml-auto flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-white/35">
                  <span className="animate-glow-pulse h-1.5 w-1.5 rounded-full bg-nexus-purple-light" />
                  Preview
                </span>
              </div>
              <div className="flex flex-col gap-3 p-5">
                {previewRows.map((r) => (
                  <div
                    key={r.label}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`h-2 w-2 rounded-full ${TONE_DOT_CLASS[r.tone]}`} />
                      <span className="text-sm text-white/90">{r.label}</span>
                    </div>
                    <span className="text-xs font-semibold text-white/60">{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <p className="mt-2 hidden text-right text-xs text-white/40 md:block">
          Illustrative preview — not live data.
        </p>
      </div>
    </section>
  );
}
